import { StyleSheet, View, Image } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { ThemedPanel } from "@/components/ThemedPanel";
import { useAuth } from "@components/auth/useAuth";
import { CustomAlert } from "@/components/ThemedAlert";
import ThemedScreen from "@/components/ThemedScreen";
import { useEffect, useState, useMemo } from "react";
import { useFirebase } from "@components/firebase/useFirebase";
import { TaskModel, Workflow } from "common247";
import { getMyWorkflows, actionTask } from "@services/firebase/workflow-functions";
import { TaskCard } from "@/components/workflow/TaskCard";
import { useThemeColor } from "@/hooks/useThemeColor";
import Constants from "expo-constants";
import { ProductVersion } from "@/versions";

/**
 * HomeScreen renders the welcome screen for authenticated users, showing pending workflow actions/tasks
 * and the status of submitted contact linkage requests.
 * @returns {React.ReactElement} The HomeScreen component.
 */
export default function IndexScreen() {
  const { user } = useAuth();
  const services = useFirebase();
  const tintColor = useThemeColor({}, "tint");
  const [activeTasks, setActiveTasks] = useState<TaskModel[]>([]);
  const [myWorkflows, setMyWorkflows] = useState<Workflow[]>([]);

  const productName = Constants.expoConfig?.name || "Memories";

  /**
   * Fetches active tasks assigned to the current user and workflows created by the user.
   */
  const fetchTasks = () => {
    if (!services) return;
    getMyWorkflows(services, {})
      .then((response) => {
        if (response.status === true) {
          setActiveTasks(response.activeTasks || []);
          setMyWorkflows(response.workflows || []);
        }
      })
      .catch((error) => {
        console.error("Error fetching workflows & tasks:", error);
      });
  };

  /**
   * Filter contact link requests initiated by or targeting this user.
   */
  const linkRequests = useMemo(() => {
    return myWorkflows.filter((w) => w.workflowType === "contact_link");
  }, [myWorkflows]);

  /**
   * Performs an accept/reject action on a specific task and updates the list.
   * @param taskId - The ID of the task to act upon.
   * @param action - The transition action ('accept' or 'reject').
   */
  const handleTaskAction = async (taskId: string, action: "accept" | "reject") => {
    if (!services) return;
    try {
      const response = await actionTask(services, { taskId, action });
      if (response.status === true) {
        fetchTasks();
      } else {
        CustomAlert.alert("Error", response.error || "Failed to execute task action");
      }
    } catch (error: any) {
      CustomAlert.alert("Error", error.message || "Failed to execute task action");
    }
  };

  useEffect(() => {
    if (!services) return;
    fetchTasks();
  }, [services]);

  return (
    <ThemedScreen>
      <ThemedView style={styles.container}>
        <ThemedPanel padding={24} elevation="low">
          <View style={styles.heroHeader}>
            <Image
              source={require("@/assets/images/adaptive-icon.png")}
              style={styles.platformIcon}
              resizeMode="contain"
              accessibilityLabel={`${productName} platform icon`}
            />
            <View style={styles.heroTextContainer}>
              <View style={styles.greetingHeader}>
                <ThemedText type="editorialTitle" style={styles.welcomeText}>
                  Welcome back to {productName}{" "}
                  <ThemedText style={styles.versionText}>v{ProductVersion}</ThemedText>
                </ThemedText>
              </View>
              <ThemedText style={styles.userNameText}>{user?.displayName || user?.email || "Guest"}</ThemedText>
              <ThemedText style={styles.introText}>Capture and preserve your precious moments with security and peace of mind.</ThemedText>
            </View>
          </View>
        </ThemedPanel>

        {activeTasks.length > 0 && (
          <ThemedView style={styles.tasksSection}>
            <ThemedText type="editorialTitle" style={styles.sectionTitle}>
              Pending Actions
            </ThemedText>
            {activeTasks.map((task) => (
              <TaskCard key={task.id} task={task} onAction={handleTaskAction} />
            ))}
          </ThemedView>
        )}

        {linkRequests.length > 0 && (
          <ThemedView style={styles.tasksSection}>
            <ThemedText type="editorialTitle" style={styles.sectionTitle}>
              Contact Link Requests
            </ThemedText>
            {linkRequests.map((req) => {
              const isPending = req.currentState === "pending_approval";
              const isApproved = req.currentState === "completed";
              const statusColor = isApproved ? tintColor : isPending ? "#F59E0B" : "#EF4444";
              const statusBg = isApproved
                ? "rgba(5, 150, 105, 0.12)"
                : isPending
                ? "rgba(245, 158, 11, 0.12)"
                : "rgba(239, 68, 68, 0.12)";
              const statusLabel = isApproved
                ? "Approved & Linked"
                : isPending
                ? "Pending Owner Approval"
                : "Declined";

              return (
                <ThemedPanel key={req.id} padding={16} elevation="low" style={styles.requestCard}>
                  <View style={styles.requestHeader}>
                    <View style={styles.requestTitleRow}>
                      <Ionicons name="person-circle-outline" size={20} color={tintColor} />
                      <ThemedText type="defaultSemiBold" style={styles.requestTitle}>
                        {req.contactDisplayName || req.name}
                      </ThemedText>
                    </View>
                    <View style={[styles.statusBadge, { backgroundColor: statusBg }]}>
                      <ThemedText style={[styles.statusBadgeText, { color: statusColor }]}>
                        {statusLabel}
                      </ThemedText>
                    </View>
                  </View>
                  <ThemedText style={styles.requestDescription}>
                    {isApproved
                      ? "The contact owner approved this link. Your account is now linked to this contact."
                      : isPending
                      ? "Awaiting approval from the contact owner to link this contact to your account."
                      : "The contact owner declined to link this contact to your account."}
                  </ThemedText>
                  {req.contactEmail && (
                    <ThemedText style={styles.requestMeta}>Email: {req.contactEmail}</ThemedText>
                  )}
                </ThemedPanel>
              );
            })}
          </ThemedView>
        )}
      </ThemedView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 16,
    gap: 16,
    backgroundColor: "transparent",
  },
  heroHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 24,
    flexWrap: "wrap",
  },
  platformIcon: {
    width: 160,
    height: 160,
  },
  heroTextContainer: {
    flex: 1,
    minWidth: 220,
  },
  greetingHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 4,
  },
  welcomeText: {
    fontSize: 22,
    fontWeight: "600",
    opacity: 0.8,
  },
  userNameText: {
    fontSize: 28,
    fontWeight: "800",
    marginBottom: 12,
  },
  introText: {
    fontSize: 15,
    lineHeight: 22,
    opacity: 0.6,
  },
  tasksSection: {
    backgroundColor: "transparent",
    marginBottom: 8,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "700",
    marginBottom: 12,
    opacity: 0.9,
  },
  requestCard: {
    marginBottom: 10,
    borderRadius: 12,
    gap: 6,
  },
  requestHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  requestTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flex: 1,
  },
  requestTitle: {
    fontSize: 15,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
  },
  statusBadgeText: {
    fontSize: 11,
    fontWeight: "700",
    textTransform: "uppercase",
  },
  requestDescription: {
    fontSize: 13,
    opacity: 0.75,
    lineHeight: 18,
  },
  requestMeta: {
    fontSize: 12,
    opacity: 0.5,
  },
  versionText: {
    fontSize: 14,
    opacity: 0.55,
    fontWeight: "500",
  },
});
